import React, { useMemo } from 'react';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { MissingMonsterPhotos } from './MissingMonsterPhotos';
import { MonsterPhotoPackCheck } from './MonsterPhotoPackCheck';
import { NpcPhotoPackCheck } from './NpcPhotoPackCheck';
import { ExtraPhotoPackCheck } from './ExtraPhotoPackCheck';
import { resolveImageEntries, useFailedImages } from '../../utils/entityImages';
import { unassignedImages } from '../../data/guide/entityImages';
import type { WorldData } from '../../types/world';

/** Panel del staff: estado de las imágenes, separado de drops, estadísticas, recetas y ubicaciones. */
export function ImageCheck({
  w
}: {
  w: WorldData;
}) {
  const {
    resolved
  } = useMemo(() => resolveImageEntries(w), [w]);
  const failed = useFailedImages();
  const rows = resolved.map(e => {
    const src = e.image?.src;
    const status = !src ? 'Sin imagen' : failed.includes(src) ? 'No carga' : e.image?.needsConfirmation ? 'Integrada · confirmar asociación' : 'Integrada';
    const id = e.kind === 'clase' ? e.classId?.toUpperCase() : e.resolvedMonsterIds.length ? e.resolvedMonsterIds.join(', ') : '—';
    const idNote = e.notInCatalog ? 'no está en 06_monsters: no se muestra' : e.ambiguous ? 'nombre con varios IDs: no se asigna' : '';
    return {
      e,
      status,
      id,
      idNote
    };
  });
  const pendingText = [...rows.filter(r => r.status !== 'Integrada').map(r => `${r.e.label} (${r.e.kind}, ID ${r.id}${r.idNote ? `, ${r.idNote}` : ''}) · ${r.status}${r.e.pending ? ` · ${r.e.pending}` : ''}`), ...unassignedImages.map(u => `Imagen sin asignar · ${u.description} · ${u.question} · ${u.src}`)].join('\n');
  return <WorldSection id="imagenes" title="Imágenes de clases, NPC y monstruos" count={rows.length} data-id="element-1315" data-mp="1sodiryxclv5r">
      <p className="mb-3 max-w-3xl text-sm text-gray-400" data-id="element-1316" data-mp="3qg9djh8y2hqb">
        Imagen proporcionada por el staff: identifica a la entidad, no confirma drops, coordenadas, estadísticas ni recetas. Si una imagen no carga, aparece aquí al intentar mostrarla en este navegador.
      </p>
      <MonsterPhotoPackCheck w={w} data-id="element-1317" data-mp="wni7kve02a31" />
      <ExtraPhotoPackCheck w={w} data-id="element-1318" data-mp="wojfiqzpyggj" />
      <NpcPhotoPackCheck w={w} data-id="element-1319" data-mp="1am82c4dcjxo8" />
      <MissingMonsterPhotos w={w} data-id="element-1320" data-mp="1anj45yn8evcp" />
      <div className="mb-3" data-id="element-1321" data-mp="1akdfgkiilvhi">
        <CopyTextButton text={pendingText || 'Sin imágenes pendientes.'} label="Copiar imágenes pendientes" data-id="element-1322" data-mp="16l4nnl71c7ol" />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1323" data-mp="1kjvwas34p15c">
        <table className="w-full min-w-[720px] text-left text-sm" data-id="element-1324" data-mp="1fluf6cp9jbwi">
          <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1325" data-mp="1fjgmgai22bs4">
            <tr data-id="element-1326" data-mp="1u95pycdmervk">
              <th className="px-4 py-3 font-semibold" data-id="element-1327" data-mp="1u9xj7gjqwdio">Imagen</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1328" data-mp="1pb48twzr92mq">Entidad</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1329" data-mp="23b58xa874615">ID</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1330" data-mp="23boponm40odl">Estado</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1331" data-mp="1yctyaxa2trhv">Procedencia y asociación</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-1332" data-mp="2hyvv6dstsu53">
            {rows.map(({
            e,
            status,
            id,
            idNote
          }) => <tr key={e.key} data-id="element-1333" data-mp="2mwf73721wutv">
                <td className="px-4 py-3" data-id="element-1334" data-mp="2muky8r420t65"><EntityImage entry={e} alt={e.label} kind={e.kind === 'monstruo' ? 'monstruo' : 'persona'} data-id="element-1335" data-mp="28xfreowinq9u" /></td>
                <td className="px-4 py-3 text-white" data-id="element-1336" data-mp="2cxzppvstszxk">{e.label}<span className="block text-xs text-gray-500" data-id="element-1337" data-mp="2d0ubprrhmst3">{e.kind}</span></td>
                <td className="px-4 py-3 font-mono text-gray-200" data-id="element-1338" data-mp="1z5ryejkba169">{id}{idNote && <span className="block font-sans text-xs text-amber-100" data-id="element-1339" data-mp="23ykyuv8qyncj">{idNote}</span>}</td>
                <td className="px-4 py-3 text-gray-200" data-id="element-1340" data-mp="1p9ow9dpgm2jb">{status}{e.pending && <span className="block text-xs text-gray-500" data-id="element-1341" data-mp="1p73t8ldfdgjz">{e.pending}</span>}</td>
                <td className="px-4 py-3 text-xs text-gray-400" data-id="element-1342" data-mp="1u8jdwcboxl1u">{e.image ? <>Imagen proporcionada por el staff · {e.image.addedOn}<span className="block" data-id="element-1343" data-mp="1galag1ywr914">{e.image.matchedBy}</span></> : '—'}</td>
              </tr>)}
            {unassignedImages.map(u => <tr key={u.src} data-id="element-1344" data-mp="1d5udhco6lt42">
                <td className="px-4 py-3" data-id="element-1345" data-mp="1d646za8fqiei"><img src={u.src} alt={u.description} loading="lazy" className="h-10 w-10 rounded-lg border border-white/10 object-cover object-[50%_30%]" data-id="element-1346" data-mp="187ssgm773jpy" /></td>
                <td className="px-4 py-3 text-white" data-id="element-1347" data-mp="13fj95p0l5hwn">Sin asignar<span className="block text-xs text-gray-500" data-id="element-1348" data-mp="zfquqvoighjd">{u.description}</span></td>
                <td className="px-4 py-3 font-mono text-gray-200" data-id="element-1349" data-mp="ugec6n4o9wml">—</td>
                <td className="px-4 py-3 text-amber-100" data-id="element-1350" data-mp="uhpf6kqan0cn">{u.question}</td>
                <td className="px-4 py-3 text-xs text-gray-400" data-id="element-1351" data-mp="pgjkihd2p30h">Imagen proporcionada por el staff · no se muestra a los jugadores</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </WorldSection>;
}